import { z } from "zod";

import {
  checklistSectionSchema,
  ctaBannerSectionSchema,
  faqSectionSchema,
  heroSectionSchema,
  iconNameSchema,
  listSectionSchema,
  pageSeoSchema,
  profileSectionSchema,
} from "@/content/schemas";
import { siteConfig } from "@/content/site";
import { validateContent } from "@/content/validateContent";

const pricingSectionSchema = z.object({
  title: z.string().min(1),
  badge: z.string().min(1),
  price: z.string().optional(),
  items: z.array(z.string().min(1)).min(1),
  primaryCta: z.object({
    label: z.string().min(1),
    href: z.string().min(1),
    external: z.boolean(),
  }),
  footerNote: z.string().optional(),
});

const statementSectionSchema = z.object({
  title: z.string().min(1),
  paragraphs: z.array(z.string().min(1)).min(1),
  highlight: z.string().optional(),
});

const previewSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  cards: z.array(
    z.object({
      title: z.string().min(1),
      description: z.string().min(1),
      icon: iconNameSchema,
      imageSrc: z.string().optional(),
      imageAlt: z.string().optional(),
      imagePosition: z.string().optional(),
      imageScale: z.string().optional(),
      previewProtection: z.enum(["light", "medium", "strong"]).optional(),
    }),
  ).min(1),
});

const aiCareerAdvantagePageSchema = z.object({
  seo: pageSeoSchema,
  hero: heroSectionSchema,
  problemSection: statementSectionSchema,
  includedSection: checklistSectionSchema,
  benefitsSection: listSectionSchema,
  systemSection: listSectionSchema,
  previewSection: previewSectionSchema,
  midPageCta: ctaBannerSectionSchema,
  audienceSection: checklistSectionSchema,
  instructorSection: profileSectionSchema,
  pricingSection: pricingSectionSchema,
  faqSection: faqSectionSchema,
  finalCta: ctaBannerSectionSchema,
});

const bundleCheckoutCta = {
  label: "Get the Complete AI Career Advantage Bundle",
  href: siteConfig.aiCareerAdvantageStripeUrl,
  external: true,
};

export const aiCareerAdvantagePageContent = validateContent(
  "aiCareerAdvantagePage",
  aiCareerAdvantagePageSchema,
  {
    seo: {
      title: "AI for Professionals: Prompts, Workflows & Templates | Tech Savvy Starts Here",
      description:
        "Learn how to use AI effectively at work with practical prompts, templates, workflows and planning tools for professionals.",
      canonicalPath: "/ai-career-advantage",
      socialImage: siteConfig.aiCareerAdvantageMockupImage,
      product: {
        name: "AI Career Advantage Bundle",
        description:
          "A digital guide and toolkit with practical AI prompts, workplace templates, workflows, planning tools, tool recommendations, and a 30-day implementation plan for professionals.",
        image: siteConfig.aiCareerAdvantageMockupImage,
        brand: siteConfig.brandName,
        price: "29.00",
        priceCurrency: "USD",
        urlPath: "/ai-career-advantage",
      },
    },
    hero: {
      eyebrow: "Digital Product",
      title: "Use AI Better at Work",
      description:
        "For professionals who use ChatGPT or AI tools but want better prompts, clearer workflows, and less trial and error.",
      secondaryDescription:
        "The AI Career Advantage Bundle helps you save time, get better results, and build a practical system for using AI in the work you already do.",
      footerNote: "Includes prompts, templates, workflows, and a 30-day plan | No technical background required | USD $29",
      primaryCta: {
        ...bundleCheckoutCta,
        label: "Get the AI Career Advantage Bundle",
      },
      secondaryCta: {
        label: "See What's Inside",
        href: "#inside",
        external: false,
      },
      media: {
        type: "image",
        imageSrc: siteConfig.aiCareerAdvantageMockupImage,
        imageAlt: "The AI Career Advantage Guide and Toolkit product bundle mockup",
        imageFit: "contain",
        floatingBadge: {
          icon: "Gift",
          title: "Complete Bundle",
          description: "Guide + Toolkit included",
        },
      },
    },
    problemSection: {
      title: "AI Should Save You Time, Not Create More Work",
      paragraphs: [
        "You open ChatGPT. You type a question. The answer sounds polished, but it is too generic.",
        "So you rewrite the prompt. Then rewrite the answer. Then double check whether the information is even correct.",
        "AI is useful, but good results depend on how you use it. The AI Career Advantage Bundle helps you build a more reliable way to use AI across your everyday work.",
      ],
    },
    includedSection: {
      title: "This Is More Than a Prompt Library",
      description:
        "You do not need hundreds of random prompts. You need a practical system for getting better outputs and knowing when your judgment still matters.",
      items: [
        "Give AI the right context",
        "Ask better questions",
        "Improve weak responses",
        "Check important information",
        "Turn useful prompts into repeatable workflows",
        "Choose the right AI tool for the task",
        "Know what to delegate and what still needs your judgment",
      ],
    },
    benefitsSection: {
      title: "See How This Fits Into Your Work",
      description:
        "Use the bundle for the real moments where AI can help you move faster without lowering quality.",
      items: [
        {
          title: "Prepare for an important meeting",
          description:
            "Use the meeting preparation framework to organize your thoughts, identify key questions, anticipate objections, and prepare your talking points.",
          icon: "Calendar",
        },
        {
          title: "Turn rough notes into a professional update",
          description:
            "Use communication prompts to structure your ideas and create a clear first draft faster, then apply the verification framework before you send it.",
          icon: "FileText",
        },
        {
          title: "Stop repeating the same AI tasks",
          description:
            "If you keep asking AI to help with the same type of work, turn the process into a reusable workflow instead of starting again each time.",
          icon: "Target",
        },
      ],
    },
    systemSection: {
      title: "Everything You Need to Put AI to Work",
      description:
        "A practical bundle for writing, planning, research, meetings, productivity, and career visibility.",
      items: [
        {
          title: "Work faster",
          description:
            "Use ready-to-adapt prompts for writing, research, planning, analysis, meetings, and professional communication. Includes 100 professional AI prompts, an executive email prompt library, and meeting preparation templates.",
          icon: "Rocket",
        },
        {
          title: "Get better AI outputs",
          description:
            "Learn how to give AI stronger context, improve weak responses, and evaluate the information it gives you across different AI tools.",
          icon: "Sparkles",
        },
        {
          title: "Build repeatable AI workflows",
          description:
            "Move beyond one-off prompts with a toolkit and weekly planner that help you identify recurring tasks and build a more structured way to use AI.",
          icon: "Layout",
        },
        {
          title: "Build your AI career advantage",
          description:
            "Identify where AI supports your work, where your judgment adds value, and how to communicate your contribution more clearly.",
          icon: "Briefcase",
        },
        {
          title: "Choose tools without the overwhelm",
          description:
            "Use the 2026 AI Tool Directory as a practical starting point for writing, research, meetings, productivity, content, and other professional tasks.",
          icon: "Lightbulb",
        },
      ],
    },
    previewSection: {
      title: "Take a Look Inside",
      description:
        "Get a quick look at the prompts, templates, and 30-day plan inside the bundle.",
      cards: [
        {
          title: "Executive Communication Prompt Library",
          description: "Ready-to-adapt prompts for announcements, policy changes, difficult conversations, requests, approvals, and negotiation.",
          icon: "MessageCircle",
          imageSrc: "/ai-career-advantage-executive-prompts-preview.png",
          imageAlt: "Preview of the Executive Communication Prompt Library from the AI Career Advantage Bundle",
          previewProtection: "strong",
          imageScale: "scale-[1.12]",
        },
        {
          title: "Meeting Preparation Templates",
          description: "A practical framework for preparing talking points, questions, objections, and follow-up notes.",
          icon: "Calendar",
          imageSrc: "/ai-career-advantage-meeting-templates-preview.png",
          imageAlt: "Preview of the Meeting Preparation Templates from the AI Career Advantage Bundle",
          previewProtection: "light",
          imageScale: "scale-[1.04]",
        },
        {
          title: "30-Day AI Career Accelerator",
          description: "A step-by-step plan for building better AI habits and applying the bundle to real work.",
          icon: "Rocket",
          imageSrc: "/ai-career-advantage-30-day-accelerator-preview.png",
          imageAlt: "Preview of the 30-Day AI Career Accelerator from the AI Career Advantage Bundle",
          previewProtection: "medium",
          imageScale: "scale-[1.08]",
        },
      ],
    },
    midPageCta: {
      title: "Stop Starting From Scratch With AI",
      description:
        "You do not need to chase every new AI tool or use AI for everything. Build a practical system for using it where it helps your work most.",
      primaryCta: {
        ...bundleCheckoutCta,
        label: "Get the Bundle",
      },
      tone: "light",
    },
    audienceSection: {
      title: "Who Is This For?",
      description:
        "The AI Career Advantage Bundle is designed for professionals who know AI is becoming part of work but want a better system for using it.",
      items: [
        "You already use ChatGPT but often get generic answers",
        "You spend time writing, researching, preparing, planning, or communicating",
        "You feel overwhelmed by the number of AI tools available",
        "You want to save time without lowering the quality of your work",
        "You want practical AI skills without learning coding or machine learning",
        "You want to build better AI habits instead of using AI randomly",
      ],
    },
    instructorSection: {
      title: "Why I Created This",
      name: "Dr. Ijeoma Ndu",
      role: "ML Engineer and AI Educator",
      description:
        "I am an AI and machine learning engineer and educator with more than a decade of experience across technology, research, and education. Most professionals do not need to become AI engineers. They need to know how to use AI well: how to provide the right context, evaluate outputs, verify important information, build useful workflows, protect sensitive information, and keep human judgment where it matters. I created the AI Career Advantage to make those skills practical.",
      icon: "User",
      imageSrc: siteConfig.headshotImage,
      imageAlt: "Dr. Ijeoma Ndu headshot",
    },
    pricingSection: {
      title: "AI Career Advantage Bundle",
      badge: "Digital download",
      price: "USD $29",
      items: [
        "AI Career Advantage Guide",
        "Prompt libraries, templates, and worksheets",
        "Weekly AI productivity planner",
        "2026 AI Tool Directory",
        "30-Day AI Career Accelerator",
      ],
      primaryCta: {
        ...bundleCheckoutCta,
        label: "Get the Bundle",
      },
      footerNote: "Instant digital access after purchase.",
    },
    faqSection: {
      title: "Questions Before You Buy?",
      description:
        "A few quick answers about what you get and how the bundle fits into your work.",
      items: [
        {
          question: "Do I need AI experience?",
          answer:
            "No. The bundle is designed for both newer AI users and professionals who already use tools such as ChatGPT but want a more structured approach.",
        },
        {
          question: "Is this only for ChatGPT?",
          answer:
            "No. The main frameworks and workflows work across several AI assistants. Some prompts may need small adjustments depending on the tool.",
        },
        {
          question: "Is this an online course?",
          answer:
            "No. This is a digital guide and toolkit that you can use at your own pace.",
        },
        {
          question: "What do I receive?",
          answer:
            "You receive the AI Career Advantage Guide plus supporting resources including prompt libraries, templates, worksheets, planning tools, the 2026 AI Tool Directory, and the 30-Day AI Career Accelerator.",
        },
        {
          question: "What is the refund policy?",
          answer:
            "Because this is an instantly delivered digital product, all sales are final. The bundle is designed to give you practical value right away through reusable prompts, templates, workflows, and planning tools you can apply to real workplace tasks.",
        },
        {
          question: "Will this help if I already use AI at work?",
          answer:
            "Yes. The bundle focuses on moving from occasional prompting to stronger context, verification, reusable workflows, and more intentional AI use.",
        },
        {
          question: "What if my company already provides AI training?",
          answer:
            "Workplace AI training is often broad, compliance-focused, or tool-specific. This bundle is a practical companion you can keep at your desk and use for your own day-to-day tasks after the training is over.",
        },
        {
          question: "What makes this different from random prompt lists online?",
          answer:
            "The bundle is organized around real workplace tasks and includes prompts, templates, worksheets, tool recommendations, and a 30-day plan so you can turn AI tips into repeatable habits.",
        },
        {
          question: "Is this useful internationally?",
          answer:
            "Yes. The main principles and workplace applications are relevant across industries and countries.",
        },
      ],
    },
    finalCta: {
      title: "Build a Better Way to Use AI at Work",
      description:
        "Use AI where it saves time. Verify what matters. Keep your judgment where it counts.",
      primaryCta: {
        ...bundleCheckoutCta,
        label: "Get the Bundle",
      },
      secondaryCta: {
        label: "Questions? Contact Us",
        href: "/contact",
        external: false,
      },
      tone: "dark",
    },
  },
);
